<template>
  <div class="w-100">
    <basic-subheader title="Model AI" />
    <b-overlay :show="loading">
      <div class="card card-custom gutter-b">
        <div class="card-body">
          <b-alert v-if="settings && !settings.claudeAvailable" show variant="warning">
            Môi trường này chưa cấu hình khoá Claude (Anthropic:ApiKey). Chọn Claude sẽ không có tác dụng, hệ thống vẫn dùng Azure.
          </b-alert>
          <p class="text-muted mb-6">
            Thay đổi có hiệu lực trong khoảng 30 giây, không cần build lại. Claude lỗi thì tự quay về Azure.
            "Theo file cấu hình" nghĩa là chưa chọn trên portal.
          </p>

          <h5 class="mb-4">Core chat (nháp trả lời nhóm Zalo)</h5>
          <b-table-simple v-if="settings" bordered responsive class="mb-8">
            <b-thead>
              <b-tr>
                <b-th>Mục</b-th>
                <b-th style="width: 260px">Đang dùng</b-th>
                <b-th style="width: 200px">Nguồn</b-th>
                <b-th style="width: 160px"></b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr>
                <b-td>Nhà cung cấp mặc định</b-td>
                <b-td>
                  <b-form-select
                    v-model="core.provider"
                    :options="providerOptions(settings.options.coreChatProviders)"
                    :disabled="!canEdit"
                    @change="save('Default', core.provider)"
                  />
                </b-td>
                <b-td>{{ sourceLabel(settings.coreChat.providerSource) }}</b-td>
                <b-td>
                  <b-button v-if="settings.coreChat.providerSource === 'portal' && canEdit" size="sm" variant="light" @click="reset('Default')">
                    Về file cấu hình
                  </b-button>
                </b-td>
              </b-tr>
              <b-tr>
                <b-td>% khách chạy Claude</b-td>
                <b-td>
                  <b-input-group append="%">
                    <b-form-input
                      v-model.number="core.percentClaude"
                      type="number"
                      min="0"
                      max="100"
                      :disabled="!canEdit"
                      @change="save('PercentClaude', String(core.percentClaude))"
                    />
                  </b-input-group>
                </b-td>
                <b-td>{{ sourceLabel(settings.coreChat.percentSource) }}</b-td>
                <b-td>
                  <b-button v-if="settings.coreChat.percentSource === 'portal' && canEdit" size="sm" variant="light" @click="reset('PercentClaude')">
                    Về file cấu hình
                  </b-button>
                </b-td>
              </b-tr>
              <b-tr v-if="settings.anthropicKey">
                <b-td>
                  Khoá Claude dùng trước
                  <div class="text-muted font-size-sm">Khoá này hết hạn mức hoặc hết tiền thì hệ thống tự chuyển sang khoá còn lại.</div>
                </b-td>
                <b-td>
                  <b-form-select
                    v-model="core.anthropicKey"
                    :options="keyOptions"
                    :disabled="!canEdit || keyOptions.length < 2"
                    @change="save('AnthropicKey', core.anthropicKey)"
                  />
                  <div v-if="settings.anthropicKey.lastUsed" class="text-muted font-size-sm mt-1">
                    Đang dùng thật: {{ keyLabel(settings.anthropicKey.lastUsed) }}
                    <span v-if="settings.anthropicKey.callsByKey">
                      (từ lúc API khởi động:
                      <span v-for="(n, k) in settings.anthropicKey.callsByKey" :key="k">{{ keyLabel(k) }} {{ n }} lượt; </span>)
                    </span>
                  </div>
                </b-td>
                <b-td>{{ sourceLabel(settings.anthropicKey.source) }}</b-td>
                <b-td>
                  <b-button v-if="settings.anthropicKey.source === 'portal' && canEdit" size="sm" variant="light" @click="reset('AnthropicKey')">
                    Về file cấu hình
                  </b-button>
                </b-td>
              </b-tr>
              <b-tr>
                <b-td>
                  Tắt Claude khẩn cấp
                  <div class="text-muted font-size-sm">Bật thì mọi chức năng quay về Azure ngay.</div>
                </b-td>
                <b-td>
                  <b-form-checkbox
                    v-model="core.killSwitch"
                    switch
                    :disabled="!canEdit"
                    @change="save('KillSwitch', String(core.killSwitch))"
                  >
                    {{ core.killSwitch ? 'Đang tắt Claude' : 'Không' }}
                  </b-form-checkbox>
                </b-td>
                <b-td>{{ sourceLabel(settings.coreChat.killSwitchSource) }}</b-td>
                <b-td>
                  <b-button v-if="settings.coreChat.killSwitchSource === 'portal' && canEdit" size="sm" variant="light" @click="reset('KillSwitch')">
                    Về file cấu hình
                  </b-button>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>

          <h5 class="mb-4">Chức năng AI khác</h5>
          <b-table-simple v-if="settings" bordered responsive>
            <b-thead>
              <b-tr>
                <b-th>Chức năng</b-th>
                <b-th style="width: 180px">Nhà cung cấp</b-th>
                <b-th style="width: 180px">Model Claude</b-th>
                <b-th style="width: 200px">Nguồn</b-th>
                <b-th style="width: 160px"></b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="f in features" :key="f.name">
                <b-td>{{ f.label }}</b-td>
                <b-td>
                  <b-form-select
                    v-model="f.provider"
                    :options="providerOptions(settings.options.featureProviders)"
                    :disabled="!canEdit"
                    @change="save(`Features.${f.name}.Provider`, f.provider)"
                  />
                </b-td>
                <b-td>
                  <b-form-select
                    v-model="f.model"
                    :options="modelOptions"
                    :disabled="!canEdit || f.provider !== 'claude'"
                    @change="save(`Features.${f.name}.Model`, f.model)"
                  />
                </b-td>
                <b-td>{{ sourceLabel(f.providerSource === 'portal' || f.modelSource === 'portal' ? 'portal' : 'config') }}</b-td>
                <b-td>
                  <b-button
                    v-if="(f.providerSource === 'portal' || f.modelSource === 'portal') && canEdit"
                    size="sm"
                    variant="light"
                    @click="resetFeature(f.name)"
                  >
                    Về file cấu hình
                  </b-button>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
const LABELS = {
  foundry: 'Azure Foundry',
  azure: 'Azure OpenAI',
  claude: 'Claude',
  haiku: 'Haiku (nhanh, rẻ)',
  sonnet: 'Sonnet (chất lượng cao)',
};

const KEY_LABELS = {
  new: 'Khoá mới (Anthropic:ApiKey2)',
  old: 'Khoá cũ (Anthropic:ApiKey)',
};

export default {
  name: 'AiModelSettings',
  data() {
    return {
      loading: false,
      settings: null,
      core: { provider: null, percentClaude: 0, killSwitch: false, anthropicKey: null },
      features: [],
    };
  },
  computed: {
    canEdit() {
      // Lưu cần quyền sửa tham số hệ thống; API từ chối nếu không có quyền
      return true;
    },
    keyOptions() {
      return (this.settings?.anthropicKey?.available || []).map((k) => ({ value: k, text: KEY_LABELS[k] || k }));
    },
    modelOptions() {
      return (this.settings?.options?.models || []).map((m) => ({ value: m, text: LABELS[m] || m }));
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    keyLabel(k) {
      return KEY_LABELS[k] || k;
    },
    providerOptions(list) {
      return (list || []).map((p) => ({ value: p, text: LABELS[p] || p }));
    },
    sourceLabel(source) {
      return source === 'portal' ? 'Chọn trên portal' : 'Theo file cấu hình';
    },
    apply(data) {
      this.settings = data;
      this.core = {
        provider: data.coreChat.provider,
        percentClaude: data.coreChat.percentClaude,
        killSwitch: data.coreChat.killSwitch,
        anthropicKey: data.anthropicKey ? data.anthropicKey.value : null,
      };
      this.features = (data.features || []).map((f) => ({ ...f }));
    },
    async loadData() {
      this.loading = true;
      try {
        const { data } = await this.$api.get('AI/LlmSettings');
        this.apply(data);
      } catch (error) {
        this.$toastr.e(error, 'ERROR');
      } finally {
        this.loading = false;
      }
    },
    async post(body, message) {
      this.loading = true;
      try {
        const { data } = await this.$api.post('AI/LlmSettings', body);
        this.apply(data);
        this.$toastr.s({ title: 'Thành công !', msg: message });
      } catch (error) {
        this.$toastr.e(error, 'ERROR');
        await this.loadData();
      } finally {
        this.loading = false;
      }
    },
    async save(key, value) {
      if (key === 'Default' && value === 'claude') {
        const ok = await this.$bvModal.msgBoxConfirm(
          'Chuyển Core chat sang Claude cho toàn bộ khách trên môi trường này?',
          { okTitle: 'Chuyển', cancelTitle: 'Huỷ', centered: true },
        );
        if (!ok) {
          this.core.provider = this.settings.coreChat.provider;
          return;
        }
      }
      await this.post({ key, value }, 'Đã lưu, có hiệu lực trong khoảng 30 giây');
    },
    async reset(key) {
      await this.post({ key, reset: true }, 'Đã quay về file cấu hình');
    },
    async resetFeature(name) {
      await this.reset(`Features.${name}.Provider`);
      await this.reset(`Features.${name}.Model`);
    },
  },
};
</script>
